import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  fetchDashboardLayout,
  resetDashboardLayout,
  saveDashboardLayout,
  type DashboardKey,
} from '../../request/dashboards/layout.ts';
import { dashboardLayoutQueryKeys } from '../../queries/dashboards/keys.ts';
import { STARTER_WIDGET_KEYS, sanitizeWidgetKeys } from '../../components/analytics/widgetRegistry.tsx';

const DASHBOARD_KEY: DashboardKey = 'main';

/**
 * The current user's chosen dashboard widgets, in display order. Until the
 * user saves a choice (or after a reset) this is the registry's starter set.
 */
export function useDashboardWidgetSelection() {
  const query = useQuery({
    queryKey: dashboardLayoutQueryKeys.layout(DASHBOARD_KEY),
    queryFn: () => fetchDashboardLayout(DASHBOARD_KEY),
  });

  const isDefault = query.data ? query.data.is_default : true;
  const widgetKeys = useMemo(() => {
    if (!query.data) return [];
    if (query.data.is_default) return STARTER_WIDGET_KEYS;
    return sanitizeWidgetKeys(query.data.layout.map((ref) => ref.widget_key));
  }, [query.data]);

  return { ...query, widgetKeys, isDefault };
}

export function useSaveDashboardWidgets() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (widgetKeys: string[]) =>
      saveDashboardLayout(
        DASHBOARD_KEY,
        widgetKeys.map((widget_key) => ({ widget_key }))
      ),
    onSuccess: (data) => {
      queryClient.setQueryData(dashboardLayoutQueryKeys.layout(DASHBOARD_KEY), data);
    },
  });
}

export function useResetDashboardWidgets() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => resetDashboardLayout(DASHBOARD_KEY),
    onSuccess: (data) => {
      queryClient.setQueryData(dashboardLayoutQueryKeys.layout(DASHBOARD_KEY), data);
    },
  });
}
